import { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import HOC from '../../components/HOC/HOC';
import styles from '../../css/style.module.css';
import page from './ManualPayouts.module.css';
import PayoutConfirmModal from './PayoutConfirmModal';
import { postApi } from '../../Repository/Api';
import endPoints from '../../Repository/apiConfig';
import { BaseUrl, getAuthHeaders } from '../../components/BaseURl/BaseUrl';
import { formatCurrency } from '../Wallet Management/staffWalletDateUtils';

const getCachedUserType = () => {
  try {
    const cached = JSON.parse(localStorage.getItem('adminData') || '{}');
    return cached?.data?.userType || cached?.userType || '';
  } catch {
    return '';
  }
};

const isFullAdmin = () => getCachedUserType() !== 'SUB_ADMIN';

const readBlobMessage = async data => {
  if (!(data instanceof Blob)) return '';
  try {
    const parsed = JSON.parse(await data.text());
    return parsed?.message || '';
  } catch {
    return '';
  }
};

const ManualPayouts = () => {
  const canUpdate = isFullAdmin();
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [confirmUpload, setConfirmUpload] = useState(false);

  const handleDownload = async () => {
    if (fromDate && toDate && fromDate > toDate) {
      toast.error('fromDate must be on or before toDate.');
      return;
    }

    setDownloading(true);
    try {
      const response = await axios.get(
        `${BaseUrl}${endPoints.downloadPendingTransactions(fromDate, toDate)}`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'pending-transactions.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Pending payouts downloaded.');
    } catch (error) {
      const message = await readBlobMessage(error?.response?.data);
      toast.error(message || 'Failed to download pending payouts!');
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);

    const response = await postApi(endPoints.updatePendingTransactionStatus, formData, {
      setLoading: setUploading,
      successMsg: 'Pending transaction statuses processed.',
      errorMsg: 'Failed to update payout statuses!',
    });

    setConfirmUpload(false);
    if (!response?.data) return;
    setUploadResult(response.data);
    setFile(null);
  };

  const updatedRows = uploadResult?.updated || [];

  return (
    <>
      <PayoutConfirmModal
        show={confirmUpload}
        title="Update payout statuses?"
        message="Each row is matched by TxnId. Paid keeps the wallet debit. Failed refunds that same amount to the wallet. Pending is left unchanged."
        confirmLabel="Upload sheet"
        loading={uploading}
        onClose={() => setConfirmUpload(false)}
        onConfirm={handleUpload}
      />
      <div className={styles.jobmanagementCotainer}>
        <div className={styles.jobtoppart}>
          <div className={styles.pagetitle}>
            <h6>Manual Payouts</h6>
          </div>
        </div>

        <div className={page.section}>
          <h6>Download pending payouts</h6>
          <p className={page.hint}>
            Downloads pending payouts. The first column is TxnId, the payout transaction id. Name,
            account, IFSC, amount, transaction date and time, and PAYMENT_STATUS follow it. The
            date is India time, as day-month-year and clock time. Leave the dates empty to include
            every pending payout.
          </p>
          <div className={page.row}>
            <div className={page.field}>
              <label htmlFor="payout-from">From date</label>
              <input
                id="payout-from"
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
              />
            </div>
            <div className={page.field}>
              <label htmlFor="payout-to">To date</label>
              <input
                id="payout-to"
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
              />
            </div>
            <div className={styles.rightbuttons2} onClick={handleDownload}>
              <p>{downloading ? 'Downloading...' : 'Download'}</p>
            </div>
          </div>
        </div>

        {canUpdate ? (
          <div className={page.section}>
            <h6>Update payment status</h6>
            <p className={page.hint}>
              Change PAYMENT_STATUS and upload the same sheet. A row updates only when TxnId is one
              pending payout. Paid keeps the debit. Failed refunds that amount. Pending is left as
              it is. Transaction date and time stay on the sheet for reference and are not used to
              match the row.
            </p>
            <div className={page.row}>
              <div className={page.field}>
                <label htmlFor="payout-file">Payout sheet</label>
                <input
                  id="payout-file"
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={e => setFile(e.target.files?.[0] || null)}
                />
              </div>
              <div
                className={styles.rightbuttons2}
                onClick={() => {
                  if (!file) {
                    toast.error('Upload the payout Excel file in the file field.');
                    return;
                  }
                  setConfirmUpload(true);
                }}
              >
                <p>{uploading ? 'Uploading...' : 'Upload'}</p>
              </div>
            </div>
            {file ? <p className={page.fileName}>{file.name}</p> : null}

            {uploadResult ? (
              <div style={{ marginTop: 16 }}>
                <div className={page.summary}>
                  <span>Updated: {uploadResult.updatedCount ?? 0}</span>
                  <span>Unchanged: {uploadResult.unchangedCount ?? 0}</span>
                  <span>Unmatched: {uploadResult.unmatchedCount ?? 0}</span>
                  <span>Invalid: {uploadResult.invalidCount ?? 0}</span>
                </div>
                {updatedRows.length ? (
                  <div className={page.tableWrap}>
                    <table>
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>TxnId</th>
                          <th>Status</th>
                          <th>Refund amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {updatedRows.map(row => (
                          <tr key={`${row.row}-${row.transactionId}`}>
                            <td>{row.row}</td>
                            <td>{row.transactionId || '—'}</td>
                            <td>{row.paymentStatus}</td>
                            <td>{row.refunded != null ? formatCurrency(row.refunded) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : (
          <p className={page.note}>
            Status updates and refunds are available to a full admin. You can still download the
            pending sheet.
          </p>
        )}
      </div>
    </>
  );
};

export default HOC(ManualPayouts);
